import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField, Typography } from "@mui/material";
import { createAhaModule, updateAhaModule } from "../../api/apiClient";
import { KEY_PATTERN, MIN_REASON, MODULE_ID_PATTERN, serverMessage, suggestKey } from "./catalogFields";

/** Create (mode="create") or edit (mode="edit") one shared module. The key and the environment IDs are not edited here. */
export default function AhaModuleDialog({ open, mode, module, environment, onSaved, onClose }) {
  const editing = mode === "edit";
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [moduleId, setModuleId] = useState("");
  const [active, setActive] = useState(true);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing ? module.name : "");
    setKey(editing ? module.key : "");
    setKeyTouched(false);
    setModuleId("");
    setActive(editing ? module.active : true);
    setReason("");
    setError("");
    setBusy(false);
  }, [open, editing, module]);

  const changed = editing && (name.trim() !== module.name || active !== module.active);
  const valid = name.trim().length > 0 && (editing
    ? changed && reason.trim().length >= MIN_REASON
    : KEY_PATTERN.test(key) && MODULE_ID_PATTERN.test(moduleId.trim()));

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      if (editing) await updateAhaModule(module.key, { name: name.trim(), active, reason: reason.trim() });
      else await createAhaModule({ key, name: name.trim(), moduleId: moduleId.trim() });
      onSaved();
    } catch (e) {
      setError(serverMessage(e, "The module could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="aha-module-title">
      <DialogTitle id="aha-module-title">{editing ? `Edit ${module.name}` : "Add AHA module"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField label="Module name" size="small" value={name}
            onChange={(e) => { setName(e.target.value); if (!editing && !keyTouched) setKey(suggestKey(e.target.value, "")); }} />
          <TextField label="Module key" size="small" value={key} disabled={editing}
            onChange={(e) => { setKey(e.target.value.toUpperCase()); setKeyTouched(true); }}
            error={!editing && key.length > 0 && !KEY_PATTERN.test(key)}
            helperText={editing ? "The key cannot change once created." : "Capital letters, digits and underscores. Locked after creation."} />
          {editing ? (
            <Typography variant="body2">
              AHA module ID in {environment}: <b>{module.moduleId || "none"}</b>. Change IDs from a product's Edit dialog.
            </Typography>
          ) : (
            <TextField label="AHA module ID" size="small" value={moduleId} onChange={(e) => setModuleId(e.target.value)}
              error={moduleId.length > 0 && !MODULE_ID_PATTERN.test(moduleId.trim())}
              helperText={`The ID AHA uses in ${environment} (digits only).`} />
          )}
          {editing && (
            <>
              <FormControlLabel label="Active" control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />} />
              {module.inUse > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Used by {module.inUse} session(s), template(s) or class setting(s): it can be renamed but not deactivated.
                </Typography>
              )}
              <TextField label="Reason for change" size="small" value={reason} onChange={(e) => setReason(e.target.value)}
                helperText={`Recorded in the audit log (at least ${MIN_REASON} characters).`} multiline minRows={2} />
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={!valid || busy}>
          {editing ? "Save changes" : "Create module"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
